import { useState } from 'react';
import {
  Box, Typography, TextField, Paper, MenuItem, Radio, RadioGroup,
  FormControlLabel, FormControl, FormLabel, Button, CircularProgress, Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { NATIONALITY_OPTIONS } from './documentCountries';

/**
 * Paso "Preguntas del cargo" del portal en vacantes MASIVAS
 * (PLAN_MASIVO_PORTAL, fase 2). Las preguntas vienen del backend
 * (position.portalForm), armadas desde la MISMA configuración de captación
 * que usa el bot: mismo orden y obligatoriedad. Las del sistema usan un
 * rótulo fijo; las propias del consultor, su texto.
 *
 * Lo que el bot interpreta con IA aquí lo resuelve el campo: nacionalidad
 * con selector, traslado Sí/No (solo si la comuna no es la del cargo y el
 * cargo no es remoto) y documentos de extranjeros como archivos.
 */

// Misma lista que el acceso al portal
export const NATIONALITIES = NATIONALITY_OPTIONS.map((n) => n.label);
const OTHER = '__other__';
export const FOREIGN_DOC_KEYS = ['doc_carnet_frente', 'doc_carnet_reverso', 'doc_visa'];
const SYSTEM_LABEL_KEYS = ['nacionalidad', 'comuna', 'experiencia', 'disponibilidad'];
const MULTILINE_KEYS = ['experiencia'];

const normalize = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

/** ¿Corresponde preguntar traslado con esta comuna? (misma regla que el backend) */
export const needsTraslado = (form, comuna) => {
  const comunaCargo = form?.traslado?.comunaCargo;
  if (!comunaCargo || form.traslado.remote || !String(comuna || '').trim()) return false;
  return normalize(comunaCargo) !== normalize(comuna);
};

export const needsForeignDocs = (form, nacionalidad) =>
  Boolean(form?.extranjeroDocs && String(nacionalidad || '').trim() && !normalize(nacionalidad).includes('chile'));

/** Claves obligatorias sin responder (el backend vuelve a validar) */
export const getCaptationMissing = (form, answers = {}) => {
  const missing = [];
  (form?.fields || []).forEach((f) => {
    if (f.required && !String(answers[f.key] || '').trim()) missing.push(f.key);
  });
  if (needsTraslado(form, answers.comuna) && !String(answers.traslado || '').trim()) missing.push('traslado');
  return missing;
};

/** Respuestas para POST /applications (solo lo que aplica) */
export const buildCaptationPayload = (form, answers = {}) => {
  const out = {};
  (form?.fields || []).forEach((f) => {
    const v = String(answers[f.key] || '').trim();
    if (v) out[f.key] = v;
  });
  if (needsTraslado(form, answers.comuna) && answers.traslado) out.traslado = answers.traslado;
  if (needsForeignDocs(form, answers.nacionalidad)) {
    FOREIGN_DOC_KEYS.forEach((k) => { if (answers[k]) out[k] = answers[k]; });
  }
  return out;
};

const PortalCaptationStep = ({
  form, answers, onChange, positionId, sessionToken, showErrors = false,
  // La nacionalidad ya se declaró al entrar al portal: no se vuelve a preguntar
  hideNationality = false,
  // Feria (PLAN_FERIAS, fase 2): respuestas reutilizadas de una postulación
  // anterior en la misma feria (comuna, disponibilidad, documentos): no se muestran
  hiddenKeys = [],
}) => {
  const { t } = useTranslation();
  const tm = (k, o) => t(`portal.massive.${k}`, o);
  const initialChoice = answers.nacionalidad
    ? (NATIONALITIES.includes(answers.nacionalidad) ? answers.nacionalidad : OTHER)
    : '';
  const [nationalityChoice, setNationalityChoice] = useState(initialChoice);
  const [uploading, setUploading] = useState({});
  const [uploadError, setUploadError] = useState({});
  const [fileNames, setFileNames] = useState({});

  const missing = showErrors ? getCaptationMissing(form, answers) : [];
  const labelFor = (f) => (SYSTEM_LABEL_KEYS.includes(f.key) ? tm(`fields.${f.key}`) : f.text);

  const handleNationality = (choice) => {
    setNationalityChoice(choice);
    onChange('nacionalidad', choice === OTHER ? '' : choice);
  };

  const handleUpload = async (key, file) => {
    if (!file) return;
    setUploading((p) => ({ ...p, [key]: true }));
    setUploadError((p) => ({ ...p, [key]: false }));
    try {
      const body = new FormData();
      body.append('document', file);
      body.append('questionKey', key);
      body.append('positionId', positionId);
      const { data } = await api.post(`/public/candidates/me/captation-document`, body, {
        headers: { 'x-candidate-token': sessionToken, 'Content-Type': 'multipart/form-data' },
      });
      if (data.response?.status_response === 'OK' && data.response.url) {
        onChange(key, data.response.url);
        setFileNames((p) => ({ ...p, [key]: data.response.fileName || file.name }));
      } else {
        setUploadError((p) => ({ ...p, [key]: true }));
      }
    } catch (err) {
      console.error('Error subiendo documento:', err);
      setUploadError((p) => ({ ...p, [key]: true }));
    } finally {
      setUploading((p) => ({ ...p, [key]: false }));
    }
  };

  // Traslado: va bajo la comuna; si la comuna se reutilizó en la feria (oculta),
  // se muestra solo, porque depende de la comuna de CADA cargo
  const renderTraslado = () => (
    <FormControl sx={{ mt: 1.5 }} error={missing.includes('traslado')}>
      <FormLabel sx={{ fontSize: '0.875rem' }}>
        {tm('traslado', { comuna: form.traslado.comunaCargo })}
      </FormLabel>
      <RadioGroup row value={answers.traslado || ''} onChange={(e) => onChange('traslado', e.target.value)}>
        <FormControlLabel value="Sí" control={<Radio />} label={t('yes')} />
        <FormControlLabel value="No" control={<Radio />} label={t('no')} />
      </RadioGroup>
    </FormControl>
  );

  const renderField = (f) => {
    const error = missing.includes(f.key);
    if (f.type === 'nationality') {
      return (
        <Box key={f.key}>
          <TextField
            select
            fullWidth
            required={f.required}
            label={labelFor(f)}
            value={nationalityChoice}
            onChange={(e) => handleNationality(e.target.value)}
            error={error && nationalityChoice !== OTHER}
          >
            {NATIONALITIES.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            <MenuItem value={OTHER}>{tm('otherNationality')}</MenuItem>
          </TextField>
          {nationalityChoice === OTHER && (
            <TextField
              fullWidth
              required={f.required}
              sx={{ mt: 1.5 }}
              label={tm('fields.nacionalidadOther')}
              value={answers.nacionalidad || ''}
              onChange={(e) => onChange('nacionalidad', e.target.value)}
              error={error}
            />
          )}
        </Box>
      );
    }
    return (
      <Box key={f.key}>
        <TextField
          fullWidth
          required={f.required}
          multiline={f.custom || MULTILINE_KEYS.includes(f.key)}
          minRows={f.custom || MULTILINE_KEYS.includes(f.key) ? 2 : undefined}
          label={labelFor(f)}
          value={answers[f.key] || ''}
          onChange={(e) => onChange(f.key, e.target.value)}
          error={error}
          inputProps={{ maxLength: 2000 }}
        />
        {f.key === 'comuna' && needsTraslado(form, answers.comuna) && renderTraslado()}
      </Box>
    );
  };

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        {tm('stepCaptation')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {(form?.fields || [])
          .filter((f) => !(hideNationality && f.type === 'nationality') && !hiddenKeys.includes(f.key))
          .map(renderField)}
        {hiddenKeys.includes('comuna') && needsTraslado(form, answers.comuna) && (
          <Box>{renderTraslado()}</Box>
        )}
      </Box>

      {needsForeignDocs(form, answers.nacionalidad) && !FOREIGN_DOC_KEYS.some((k) => hiddenKeys.includes(k)) && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>{tm('docsTitle')}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>{tm('docsHint')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {FOREIGN_DOC_KEYS.map((key) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ minWidth: 220 }}>{tm(`docs.${key}`)}</Typography>
                <Button
                  component="label"
                  size="small"
                  variant="outlined"
                  disabled={Boolean(uploading[key])}
                  startIcon={uploading[key] ? <CircularProgress size={14} color="inherit" /> : null}
                >
                  {answers[key] ? tm('replace') : tm('upload')}
                  <input
                    hidden
                    type="file"
                    accept=".pdf,.doc,.docx,image/jpeg,image/png"
                    onChange={(e) => handleUpload(key, e.target.files && e.target.files[0])}
                  />
                </Button>
                {answers[key] && (
                  <Typography variant="caption" color="success.main">
                    {tm('uploaded')}{fileNames[key] ? ` · ${fileNames[key]}` : ''}
                  </Typography>
                )}
                {uploadError[key] && (
                  <Typography variant="caption" color="error">{tm('uploadError')}</Typography>
                )}
              </Box>
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default PortalCaptationStep;
